import {
  availableEffortLevels,
  defaultChatModel,
  defaultFastModel,
  getAIProvider,
} from "../../../shared/aiProviders";
import type { CredentialField } from "../../../shared/aiProviders/manifest";
import { effortLabel } from "../../lib/effort";
import { ConnectorSelect } from "../ConnectorSelect";
import type { AppInfo } from "../ConnectorRow";
import { Dropdown } from "../Dropdown";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { Text } from "../Text";
import { Textarea } from "../Textarea";
import { DropdownOpener } from "../Settings/shared";
import type { AIConfigDraft } from "./aiProviderDraft";
import { FormCard, ModalField, fullWidthStyle } from "./modalForm";

const monospaceStyle = { fontFamily: "ui-monospace, monospace" } as const;

function CredentialInput({
  field,
  value,
  onChange,
  reusePrior,
  disabled,
}: {
  field: CredentialField;
  value: string;
  onChange: (value: string) => void;
  reusePrior: boolean;
  disabled: boolean;
}) {
  const description =
    field.sensitive && reusePrior
      ? `Leave blank to keep the current ${field.label.toLowerCase()}.`
      : field.description;
  const placeholder =
    field.sensitive && reusePrior ? "••••••••" : field.placeholder;

  return (
    <ModalField label={field.label} description={description}>
      {field.multiline ? (
        <Textarea
          right={null}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          minRows={6}
          maxRows={12}
          disabled={disabled}
          style={monospaceStyle}
        />
      ) : (
        <Input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder}
          autoComplete={field.sensitive ? "off" : undefined}
          disabled={disabled}
          secret={field.sensitive}
          secretIsSet={reusePrior}
          style={fullWidthStyle}
        />
      )}
    </ModalField>
  );
}

function ModelSelect({
  value,
  options,
  blankLabel,
  disabled,
  onChange,
}: {
  value: string;
  options: string[];
  blankLabel: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  // Keep a previously-saved model selectable even if it's no longer in the list.
  const allOptions =
    value && !options.includes(value) ? [value, ...options] : options;
  return (
    <Dropdown
      opener={
        <DropdownOpener type="button" disabled={disabled}>
          <Text variant="text-regular">{value || blankLabel}</Text>
          <span style={{ opacity: 0.3 }}>
            <Icon name="chevron-down" size={12} />
          </span>
        </DropdownOpener>
      }
      align="bottom-left"
      sections={[
        {
          items: [
            {
              type: "button" as const,
              left: null,
              label: blankLabel,
              right:
                value === "" ? (
                  <Icon name="check" size={12} color="var(--text-secondary)" />
                ) : undefined,
              onClick: () => onChange(""),
            },
            ...allOptions.map((model) => ({
              type: "button" as const,
              left: null,
              label: model,
              right:
                model === value ? (
                  <Icon name="check" size={12} color="var(--text-secondary)" />
                ) : undefined,
              onClick: () => onChange(model),
            })),
          ],
        },
      ]}
    />
  );
}

type Props = {
  draft: AIConfigDraft;
  reusePrior: boolean;
  disabled: boolean;
  onChange: (draft: AIConfigDraft) => void;
  connectors: AppInfo[];
  selectedConnectorName: string | null;
  allowedModels: string[];
  onConnectorChange: (connectedAppId: string | null) => void;
  onAddConnector: () => void;
  managed: boolean;
};

export function AiProviderSettingsTab({
  draft,
  reusePrior,
  disabled,
  onChange,
  connectors,
  selectedConnectorName,
  allowedModels,
  onConnectorChange,
  onAddConnector,
  managed,
}: Props) {
  const draftProvider = getAIProvider(draft.provider);
  const effectiveChatModel =
    draft.chatModel.trim() || defaultChatModel(draft.provider);
  const availableEfforts = availableEffortLevels(
    draftProvider,
    effectiveChatModel
  );
  const usesConnector = draft.connectedAppId != null;

  const patch = (next: Partial<AIConfigDraft>) =>
    onChange({ ...draft, ...next });

  return (
    <FormCard>
      {draftProvider.credentialFields
        // The api key comes from a connector, never typed here; and when the
        // connector owns the endpoint, the base URL comes from it too.
        .filter(
          (field) =>
            field.key !== "apiKey" &&
            !(field.sensitive && usesConnector) &&
            !(field.key === "baseUrl" && draftProvider.endpointFromConnector)
        )
        .map((field) => (
          <CredentialInput
            key={field.key}
            field={field}
            value={draft.credentials[field.key] ?? ""}
            onChange={(value) =>
              patch({
                credentials: { ...draft.credentials, [field.key]: value },
              })
            }
            reusePrior={reusePrior}
            disabled={disabled}
          />
        ))}

      {!managed && (
        <ModalField label="Connector">
          <ConnectorSelect
            apps={connectors}
            value={draft.connectedAppId}
            selectedName={selectedConnectorName}
            onChange={onConnectorChange}
            placeholder="Choose connector"
            addNew={[{ label: "New connector", onClick: onAddConnector }]}
          />
        </ModalField>
      )}

      <ModalField
        label="Chat model"
        description={
          allowedModels.length > 0
            ? "Choose from the models this connector allows."
            : draftProvider.requiresChatModel
              ? "Required. Use the model ID exposed by your endpoint."
              : "Leave blank for the provider default."
        }
      >
        {allowedModels.length > 0 ? (
          <ModelSelect
            value={draft.chatModel}
            options={allowedModels}
            blankLabel="Provider default"
            disabled={disabled}
            onChange={(value) => patch({ chatModel: value })}
          />
        ) : (
          <Input
            value={draft.chatModel}
            onChange={(event) => patch({ chatModel: event.target.value })}
            placeholder={defaultChatModel(draft.provider)}
            disabled={disabled}
            style={fullWidthStyle}
          />
        )}
      </ModalField>

      <ModalField
        label="Fast model"
        description="Leave blank to use the chat model."
      >
        {allowedModels.length > 0 ? (
          <ModelSelect
            value={draft.fastModel}
            options={allowedModels}
            blankLabel="Use chat model"
            disabled={disabled}
            onChange={(value) => patch({ fastModel: value })}
          />
        ) : (
          <Input
            value={draft.fastModel}
            onChange={(event) => patch({ fastModel: event.target.value })}
            placeholder={defaultFastModel(draft.provider)}
            disabled={disabled}
            style={fullWidthStyle}
          />
        )}
      </ModalField>

      {availableEfforts.length > 0 && (
        <ModalField
          label="Effort"
          description="Default effort for this model. Users can override it per chat."
        >
          <Dropdown
            opener={
              <DropdownOpener type="button" disabled={disabled}>
                <Text variant="text-regular">{effortLabel(draft.effort)}</Text>
                <span style={{ opacity: 0.3 }}>
                  <Icon name="chevron-down" size={12} />
                </span>
              </DropdownOpener>
            }
            align="bottom-left"
            sections={[
              {
                items: [
                  {
                    type: "button" as const,
                    left: null,
                    label: "Provider default",
                    right:
                      draft.effort == null ? (
                        <Icon
                          name="check"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      ) : undefined,
                    onClick: () => patch({ effort: null }),
                  },
                  ...availableEfforts.map((level) => ({
                    type: "button" as const,
                    left: null,
                    label: effortLabel(level),
                    right:
                      level === draft.effort ? (
                        <Icon
                          name="check"
                          size={12}
                          color="var(--text-secondary)"
                        />
                      ) : undefined,
                    onClick: () => patch({ effort: level }),
                  })),
                ],
              },
            ]}
          />
        </ModalField>
      )}
    </FormCard>
  );
}
